import React, { type ChangeEvent, type FC, useState } from "react";
import { useModal } from "react-modal-hook";
import router from "next/router";
import { server } from "../../../state";
import Modal from "../../ui/design-system/Modal";
import { Input } from "../../ui/design-system/Input";
import useFormValidator from "../../../hooks/useFormValidator";
import Select from "../../ui/design-system/Select";
import { AlertBanner } from "../../ui/AlertBanner";
import Button from "../../ui/design-system/Button";
import type { Roles } from "../../../state/server";

interface Props {
  hide(): void;
}

type FormValues = {
  email: string;
  role: Roles | "member";
};

export const useMemberInviteModal = () => {
  const [show, hide] = useModal(() => <MemberInviteModal {...{ hide }} />);
  return { show, hide };
};

const MemberInviteModal: FC<Props> = ({ hide }) => {
  const users = server.useUsers();
  const account = server.useAccount();

  const [errorMessage, setErrorMessage] = useState("");
  const { values, handleChange } = useFormValidator<FormValues>(
    {
      email: "",
      role: "member",
    },
    {
      email: {
        required: true,
      },
      role: {
        required: true,
      },
    }
  );
  const { email, role } = values;
  const roleOptions = [
    { value: "member", label: "Member" },
    { value: "admin", label: "Admin" },
  ];

  return (
    <Modal
      hideModal={hide}
      size="small"
      agree={"Invite"}
      loadingMessage={"Sending invite..."}
      successMessage="Invite sent!"
      title={"Invite to Sensible"}
      onAgree={async () => {
        // Reformat role to be undefined if member for API
        let userRole = role as Roles | undefined;
        if (role === "member") {
          userRole = undefined;
        }
        if (!email) {
          throw { message: "Email is requried" };
        }
        try {
          if (users === undefined) return;
          await server.inviteAccountUser(email, userRole);
        } catch (error: any) {
          setErrorMessage(
            error.response.result?.errors ??
              error.response.result.message ??
              error.response.result
          );
          throw error;
        }
      }}
    >
      <div>
        <div className="flex flex-col gap-ds-4 w-full">
          {errorMessage && (
            <div className="bg-red-100 text-red-700 p-2 mt-2 rounded">
              {errorMessage}
            </div>
          )}

          {account && account.plan == "free" && (
            <AlertBanner className="bg-warning-light text-black">
              <div className="flex flex-col items-start gap-4">
                <div>
                  <p>Invites are limited on your current plan.</p>
                  <p>Upgrade to invite more members.</p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    hide();
                    router.push("/account/?t=subscription");
                  }}
                >
                  Upgrade now
                </Button>
              </div>
            </AlertBanner>
          )}
          <Input
            name={"email"}
            type={"email"}
            label={"Email"}
            autoFocus
            placeholder={"Type email address"}
            fullWidth
            value={email}
            onChange={(e) => handleChange(e, "email")}
            required
            rightIcon={
              <Select
                options={roleOptions}
                value={role || "member"} // default to member
                buttonClassName={"!border-none !h-8"}
                onChange={(value: string) => {
                  const event = {
                    target: { value },
                  } as ChangeEvent<HTMLInputElement>;
                  handleChange(event, "role");
                }}
              />
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default MemberInviteModal;
